// ─── Bot / headless-browser detection ────────────────────────────────────────
//
// We check for injected WebDriver globals and known headless-only signals.
// A single positive signal is enough: navigator.webdriver is set by every
// mainstream automation tool (Selenium, Puppeteer, Playwright) unless they
// apply stealth patches.

type AnyWindow = Window & typeof globalThis & Record<string, unknown>;

const BOT_CHECKS: Array<() => boolean> = [
  // W3C WebDriver protocol — most reliable signal
  () => navigator.webdriver === true,
  // WebDriver attribute injected by chromedriver
  () => document.documentElement.hasAttribute('webdriver'),
  // Legacy Selenium injected globals
  () => '__selenium_evaluate' in window,
  () => '__webdriver_evaluate' in window,
  () => '__fxdriver_evaluate' in window,
  () => '__driver_evaluate' in window,
  () => '_Selenium_IDE_Recorder' in window,
  // PhantomJS
  () => '_phantom' in window || '__phantom' in window || 'callPhantom' in window,
  // Nightmare.js (Electron-based)
  () => '__nightmare' in window,
  // Chrome headless without stealth: window.chrome is absent despite Chrome UA
  () => {
    const ua = navigator.userAgent.toLowerCase();
    const isChromiumUA = ua.includes('chrome') && !ua.includes('edg/');
    return isChromiumUA && !('chrome' in window);
  },
];

/** Returns the number of positive bot signals (0 = clean browser). */
export function countBotSignals(): number {
  return BOT_CHECKS.reduce((n, fn) => {
    try { return fn() ? n + 1 : n; } catch { return n; }
  }, 0);
}

/**
 * True when at least one unambiguous automation signal is detected.
 * A single webdriver / WebDriver-attribute hit is conclusive; the
 * Chrome-globals check is gated on a matching UA to prevent false positives
 * on Firefox or custom Chromium builds.
 */
export function isAutomatedBrowser(): boolean {
  return countBotSignals() >= 1;
}

// ─── Sliding-window API rate limiter ─────────────────────────────────────────
//
// Prevents a single session (tab) from hammering the backend.
// Normal browsing: 60–80 calls / 60 s at most.
// Limit is set to 150 / 60 s, giving a comfortable safety margin for real
// users while stopping naive scrapers.

const RATE_WINDOW_MS = 60_000;
const RATE_MAX = 150;

export class RateLimitError extends Error {
  constructor(public readonly retryAfterMs: number) {
    super(`Too many requests — please wait ${Math.ceil(retryAfterMs / 1_000)} s.`);
    this.name = 'RateLimitError';
  }
}

// Circular-ish timestamp log — trimmed on every check so it stays small.
const _ts: number[] = [];

/**
 * Call before every outgoing fetch.
 * Prunes expired timestamps, then throws `RateLimitError` if the window is
 * full.  Normal callers do not need to catch this explicitly; the top-level
 * handler in main.ts surfaces a toast.
 */
export function checkApiRate(): void {
  const now = Date.now();
  // Drop timestamps older than the window
  let i = 0;
  while (i < _ts.length && now - _ts[i] > RATE_WINDOW_MS) i++;
  if (i > 0) _ts.splice(0, i);

  if (_ts.length >= RATE_MAX) {
    // oldest timestamp still inside the window
    const waitMs = RATE_WINDOW_MS - (now - _ts[0]);
    throw new RateLimitError(Math.max(waitMs, 1000));
  }
  _ts.push(now);
}

/** Remaining capacity in the current window (informational). */
export function apiRateRemaining(): number {
  const now = Date.now();
  return RATE_MAX - _ts.filter((t) => now - t <= RATE_WINDOW_MS).length;
}
